import React, { useMemo } from "react";

import { Stack, Typography, useTheme } from "@mui/material";

import { formatName } from "igfd-ui/src/utils/stringUtils";

import { useRestaurantContext } from "../../hooks/useRestaurantContext";
import { ProductListItem } from "../dish-drawer/product-list-item";

interface SubProductListProps {
  subProducts: { id: string }[];
  filter: string;
  selectedAllergenIds: string[];
}

/**
 * SubProductList displays a filtered list of directly linked sub-products for a given product.
 *
 * It supports:
 * - Searching by product name via the `filter` prop.
 * - Filtering by selected allergens using `selectedAllergenIds`, showing sub-products containing any selected allergen.
 */
export const SubProductList = ({
  subProducts,
  filter,
  selectedAllergenIds,
}: SubProductListProps) => {
  const { palette } = useTheme();
  const { linkedItems } = useRestaurantContext();

  const filteredSubProducts = useMemo(() => {
    return subProducts.filter((prod) => {
      const product = linkedItems?.products[prod.id];
      if (!product) return false;

      const matchesFilter =
        !filter || product.name.toLowerCase().includes(filter.toLowerCase());

      const matchesAllergens = selectedAllergenIds?.length
        ? product.allAllergens?.some((allergen) =>
            selectedAllergenIds.includes(allergen.igfdTaxAllergenId)
          )
        : true;

      return matchesFilter && matchesAllergens;
    });
  }, [filter, selectedAllergenIds, subProducts, linkedItems]);

  if (!filteredSubProducts?.length) return null;

  return (
    <Stack width="100%">
      <Typography variant="labelRMedium" mb={1} color="text.secondary">
        {`${filteredSubProducts.length} ${
          filteredSubProducts.length === 1 ? "Sub-product" : "Sub-products"
        }`}
      </Typography>

      {filteredSubProducts.map(({ id }, index) => {
        const product = linkedItems?.products[id];
        const hasBorder = index < filteredSubProducts.length - 1;

        return (
          <ProductListItem
            key={product?.name || id}
            title={formatName(product?.name || "Unknown")}
            preference={product?.mostRestrictivePreference?.preferenceName}
            allergenCount={product?.allAllergens?.length || 0}
            hasBorder={hasBorder}
          />
        );
      })}
    </Stack>
  );
};
